import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { settingsAPI } from "../../services/api";

const AISettings = ({ configurationId }) => {
  const [endpoint, setEndpoint] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [modelName, setModelName] = useState("gpt-4");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    // Clear previous state when configuration changes
    setError("");
    setSuccess("");

    if (configurationId) {
      loadConfig();
    } else {
      setEndpoint("");
      setApiKey("");
      setModelName("gpt-4");
    }
  }, [configurationId]);

  const loadConfig = async () => {
    if (!configurationId) {
      setEndpoint("");
      setApiKey("");
      setModelName("gpt-4");
      return;
    }
    try {
      setError("");
      const response = await settingsAPI.getAIConfig(configurationId);
      if (response.data) {
        setEndpoint(response.data.azure_foundry_endpoint);
        setApiKey(response.data.api_key);
        setModelName(response.data.model_name || "gpt-4");
      } else {
        setEndpoint("");
        setApiKey("");
        setModelName("gpt-4");
      }
    } catch (err) {
      console.error("Failed to load AI config:", err);
      setError(err.response?.data?.error || "Failed to load AI configuration.");
      setEndpoint("");
      setApiKey("");
      setModelName("gpt-4");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!configurationId) {
      setError("Please select a configuration first");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await settingsAPI.createAIConfig({
        azure_foundry_endpoint: endpoint,
        api_key: apiKey,
        model_name: modelName,
        configuration_id: configurationId,
      });
      setSuccess("AI configuration saved successfully!");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save AI configuration");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        AI Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure Azure Foundry AI model endpoint for change request analysis.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Azure Foundry Endpoint"
          variant="outlined"
          margin="normal"
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          required
          placeholder="https://your-endpoint.openai.azure.com/openai/deployments/your-deployment"
          helperText="For Azure AI Studio: Enter the full deployment endpoint URL (e.g., https://your-resource.services.ai.azure.com/openai/deployments/your-deployment-name). For project URLs, the deployment name from Model Name field will be used."
        />
        <TextField
          fullWidth
          label="API Key"
          type="password"
          variant="outlined"
          margin="normal"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          required
        />
        <TextField
          fullWidth
          label="Deployment/Model Name"
          variant="outlined"
          margin="normal"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
          placeholder="gpt-4"
          helperText="For Azure AI Studio project URLs: Enter the actual deployment name (not the model name). For full deployment URLs, this field is optional."
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<Save />}
          sx={{ mt: 2 }}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Configuration"}
        </Button>
      </Box>
    </Paper>
  );
};

export default AISettings;
